// Phase 4 (api-phase4.md "AI watches"): the watches on the open event and on my parked events, from
// GET /api/events/:id/watches. Operators' own park watches work at every level, so this does not depend on the agent
// panel (operators at a Level 0 site never see it). Refetched when the stream says the event changed (a watch set,
// confirming, hit, expired or cancelled bumps the event) or the agent acted on it.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type Watch } from '../api.ts';
import { useStreamEvent } from '../stream.tsx';

export interface AiWatches {
  /** undefined until loaded. */
  get: (eventId: string) => Watch[] | undefined;
  reload: (eventId: string) => void;
}

export function useAiWatches(ids: readonly string[], on: boolean): AiWatches {
  const [map, setMap] = useState<Record<string, Watch[]>>({});
  const idsRef = useRef<ReadonlySet<string>>(new Set());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const key = on ? [...new Set(ids)].sort().join(',') : '';

  const load = useCallback((id: string) => {
    api.watches(id).then(
      (l) => setMap((m) => ({ ...m, [id]: l })),
      () => undefined,
    );
  }, []);

  const reload = useCallback(
    (id: string) => {
      const t = timers.current.get(id);
      if (t) clearTimeout(t);
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id);
          if (idsRef.current.has(id)) load(id);
        }, 250),
      );
    },
    [load],
  );

  useEffect(() => {
    const next = new Set(key ? key.split(',') : []);
    const prev = idsRef.current;
    idsRef.current = next;
    for (const id of next) if (!prev.has(id)) load(id);
  }, [key, load]);

  useEffect(() => {
    const m = timers.current;
    return () => m.forEach((t) => clearTimeout(t));
  }, []);

  useStreamEvent('event', (e) => {
    if (idsRef.current.has(e.id)) reload(e.id);
  });
  useStreamEvent('agent', (e) => {
    if (e?.eventId && idsRef.current.has(e.eventId)) reload(e.eventId);
  });
  useStreamEvent('watch_hit', (e) => {
    if (e?.eventId && idsRef.current.has(e.eventId)) reload(e.eventId);
  });
  useStreamEvent('raise', (e) => {
    if (idsRef.current.has(e.eventId)) reload(e.eventId);
  });

  const get = useCallback((id: string) => map[id], [map]);
  return { get, reload };
}
